import { listSubmissionReport, SubmissionSummary } from "@rubi/api-generated";

// Statuses that represent records on their way out — never warn against these.
const DELETED_STATUSES = ["pending_deletion", "pending_deletion_public"];

export interface FindIncidentNumberDuplicatesArgs {
  /** The incident number to look up (trimmed internally). */
  value: string;
  /** The record currently being edited (excluded from matches). */
  submissionId: string | undefined;
  orgId: string | undefined;
  /** Linked supplemental submission IDs to exclude from matches. */
  excludeIds: string[];
  /** When false (e.g. closed/exported record) the lookup is skipped. */
  enabled?: boolean;
}

/**
 * Advisory lookup for other RubiREPORT records sharing an RMS incident number.
 * Resolves to the matching records so a Save handler can `await` it and hold
 * the save until the officer acknowledges any duplicates. Resolves to `[]`
 * (rather than throwing) for every skip/failure path so it never blocks a save.
 *
 * NOTE: the Rust `incident_number` filter is a case-sensitive exact match, so
 * this only detects exact-case, whole-string duplicates (documented v1 limit).
 * A blank value resolves to `[]`, which also covers the auto-generated-number
 * case.
 */
export async function findIncidentNumberDuplicates({
  value: rawValue,
  submissionId,
  orgId,
  excludeIds,
  enabled = true,
}: FindIncidentNumberDuplicatesArgs): Promise<SubmissionSummary[]> {
  const value = rawValue.trim();

  if (!enabled || !value || !orgId || !submissionId) return [];

  try {
    const res = await listSubmissionReport({
      query: {
        time_created_min: null,
        time_created_max: null,
        time_updated_min: null,
        time_updated_max: null,
        status: null,
        source: null,
        first_name: null,
        last_name: null,
        email: null,
        phone_number: null,
        secondary_phone_number: null,
        incident_number: value,
        tracking_number: null,
        limit: 20,
        offset: 0,
        survey_name: null,
        search: null,
        assigned_to: null,
        image_search_id: null,
        image_similarity: null,
        sort_order: "desc",
        org_id: orgId,
      },
    });

    const exclude = new Set([submissionId, ...excludeIds]);
    return (res.data?.submissions ?? []).filter(
      (s) => !exclude.has(s.id) && !DELETED_STATUSES.includes(s.status),
    );
  } catch (error) {
    // Advisory only — never block the officer's edit if the lookup fails.
    console.error("Incident number duplicate check failed:", error);
    return [];
  }
}
